<script lang="ts" setup>
import type { CartItem } from '~/types/Cart';
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';

const isCartShareModalOpen = defineModel<boolean>({ default: false });

const props = defineProps<{ items: CartItem[]; isRecipient?: boolean }>();

const { isMobile } = useBreakpoints();

const title = computed(() => {
  return props.isRecipient
    ? 'Список товаров'
    : isMobile.value
      ? 'Поделиться'
      : 'Поделиться выбранными товарами вашей корзины';
});

const sumWithoutDiscount = computed(() => {
  let sum = 0;

  props.items.forEach((item) => {
    sum += item.data.old_price ?? item.data.price;
  });

  return sum;
});

const total = computed(() => {
  let sum = 0;

  props.items.forEach((item) => {
    sum += item.data.price;
  });

  return sum;
});

const productsCountPluralized = computed(
  () => `${props.items.length} ${getNoun(props.items.length, 'товар', 'товара', 'товаров')}`,
);

const { appUrl } = useRuntimeConfig().public;
const { copy, copied, isSupported } = useClipboard();
const { sendShareCartEventToDataLayer } = useGtmModule();

async function copyShareLink() {
  if (!isSupported.value) return;

  await copy(appUrl + '/cart?share=' + props.items.map((item) => item.id).join(','));

  await sendShareCartEventToDataLayer(props.items.map((i) => i.data));

  isCartShareModalOpen.value = false;
}

const isLoading = ref(false);
const cartStore = useCartStore();
const route = useRoute();

async function addToCart() {
  isLoading.value = true;

  if (route.query.share) delete route.query.share;
  window.history.replaceState(window.history.state, '', '/cart');

  cartStore.countedCartItems.forEach((item) => {
    const matchedItem = props.items.find((i) => i.id === item.id);
    if (matchedItem) {
      item.count += 1;
    }
  });

  const newItems = props.items
    .filter((item) => !cartStore.countedCartItems.some((i) => i.id === item.id))
    .map((item) => ({
      id: item.id,
      count: item.count,
    }));

  await cartStore.changeCartState([...cartStore.countedCartItems, ...newItems]);

  isLoading.value = false;
  isCartShareModalOpen.value = false;
}

const toast = useToast();

watch(
  () => copied.value,
  () => {
    if (copied.value)
      toast.info(
        { component: ToastComponent, props: { text: 'Ссылка скопирована' } },
        {
          timeout: 2000,
          hideProgressBar: true,
          icon: false,
        },
      );
  },
);
</script>

<template>
  <BaseModal
    v-model="isCartShareModalOpen"
    @close="isCartShareModalOpen = false"
  >
    <div
      v-if="items.length"
      class="flex flex-col gap-3 px-2"
    >
      <div class="cart-share-modal-list-item__header">
        <component
          :is="isMobile ? 'h1' : 'h2'"
          class="mr-10"
        >
          {{ title }}
        </component>

        <div class="hidden md:block font-bold text-hell-black-40">
          {{ productsCountPluralized }}
        </div>

        <div
          v-if="!isRecipient"
          class="block md:hidden text-xs text-hell-black-40"
        >
          Выбранными товарами вашей корзины
        </div>
      </div>

      <div
        v-for="item in items"
        class="cart-share-modal-list-item"
      >
        <AppLink
          :href="item.data.uri"
          class="cart-share-modal-list-item__image-box"
        >
          <picture v-if="item.data.offer_images.length && item.data.offer_images[0].url">
            <source
              v-if="item.data.offer_images[0].conversions_webp.thumb_url_180"
              :srcset="`${item.data.offer_images[0].conversions_webp.thumb_url_180}, ${item.data.offer_images[0].conversions_webp.thumb_url_360} 2x`"
              media="(max-width: 360px)"
              type="image/webp"
            />

            <source
              v-if="item.data.offer_images[0].conversions_webp.thumb_url_360"
              :srcset="`${item.data.offer_images[0].conversions_webp.thumb_url_360}, ${item.data.offer_images[0].conversions_webp.thumb_url_740} 2x`"
              type="image/webp"
            />

            <source
              v-if="item.data.offer_images[0].conversions.thumb_url_360"
              :srcset="item.data.offer_images[0].conversions.thumb_url_360"
              type="image/jpeg"
            />

            <img
              :alt="item.data.product_name_medium"
              :src="item.data.offer_images[0].url"
              class="cart-share-modal-list-item__image"
              loading="lazy"
            />
          </picture>

          <SvgoNoPhotoSmall
            v-else
            :font-controlled="false"
            class="w-6 h-6"
            filled
          />
        </AppLink>

        <AppLink
          :to="item.data.uri"
          class="cart-share-modal-list-item__name"
        >
          <div class="cart-share-modal-list-item__price">
            <div class="text-sm font-bold">{{ item.data.price.toLocaleString('ru-RU') }} ₽</div>
            <div
              v-if="item.data.old_price"
              class="line-through text-xs text-hell-black-60"
            >
              {{ item.data.old_price.toLocaleString('ru-RU') }} ₽
            </div>
          </div>

          <div>{{ item.data.product_name_medium }}</div>

          <div
            v-if="item.data.displayable_offer_properties.find((prop) => prop.name === 'Размер')"
            class="text-xs text-hell-black-40"
          >
            Размер {{ item.data.displayable_offer_properties.find((prop) => prop.name === 'Размер')?.value }}
          </div>
        </AppLink>
      </div>

      <div class="flex gap-10 my-5 items-center justify-between md:justify-start">
        <div class="flex flex-col md:ml-2">
          <span
            v-if="sumWithoutDiscount < total"
            class="line-through text-sm text-hell-black-60"
          >
            {{ sumWithoutDiscount.toLocaleString('ru-RU') }} ₽
          </span>

          <span class="font-bold"> {{ total.toLocaleString('ru-RU') }} ₽</span>
        </div>

        <TheBtn
          v-if="isRecipient"
          :is-loading="isLoading"
          :size="isMobile ? 'md' : 'lg'"
          @click="addToCart"
        >
          Добавить в корзину
        </TheBtn>

        <TheBtn
          v-else
          :size="isMobile ? 'md' : 'lg'"
          @click="copyShareLink"
        >
          Поделиться
        </TheBtn>
      </div>
    </div>

    <div
      v-else
      class="flex flex-col gap-3 px-3 py-5 max-w-xl text-center"
    >
      <p>К сожалению, товары из этой ссылки отсутствуют в продаже.</p>

      <p>Но у нас еще много интересного в каталоге!</p>

      <TheBtn
        class="mt-3"
        color="outline"
        size="md"
        to="/catalog"
      >
        Перейти в каталог
      </TheBtn>
    </div>
  </BaseModal>
</template>

<style lang="scss" scoped>
.cart-share-modal-list-item {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 4px 0;

  &__header {
    display: flex;
    flex-direction: column;
  }

  &__image-box {
    position: relative;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 50px;
    border-radius: 12px;
    background-color: $hell-black-5;
    cursor: pointer;
  }

  &__image {
    object-fit: contain;
    width: 50px;
    height: 50px;
    border-radius: 12px;
  }

  &__name {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 4px;
    font-size: $text-xs;
  }

  &__price {
    display: flex;
    gap: 4px;
    align-items: center;
  }
}
</style>
